import React, { useEffect, useMemo } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { useForm } from 'react-hook-form';
import { validateCpf } from '../utils/validateCpf';
import { validateRFCOrCURP } from '../utils/validateRFCOrCURP';

const ShippingAddressForm = () => {
    const postUrl = '/events/confirm-address/' + window.shippingForm.missionId;
    const shippingForm = window.shippingForm;
    const confirmAddress = JSON.parse(shippingForm.confirmAddress);
    const firstName = shippingForm.firstName;
    const lastName = shippingForm.lastName;
    const shippingCountries = Object.entries(JSON.parse(shippingForm.shippingCountries));
    const shippingDialCodes = shippingForm.shippingDialCodes;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setError,
        setValue,
    } = useForm();
    const country = watch('delivery_country');

    const maxCompanyLength = showCustomsReference ? 13 : 30;

    const showCustomsReference = useMemo(() => {
        return ['Brazil', 'Mexico'].includes(country);
    }, [country]);

    const customReferencePlaceholder = useMemo(() => {
        if (country === 'Brazil') {
            return 'CPF (000.000.000-00)';
        }
        if (country === 'Mexico') {
            return 'RFC/CURP';
        }
        return '';
    }, [country]);

    useEffect(() => {
        if (showCustomsReference) {
            setValue('delivery_customs_reference', confirmAddress.delivery_customs_reference ?? '');
            return;
        }
        setValue('delivery_customs_reference', '');
    }, [country]);

    const onSubmit = async (data) => {
        const formData = new FormData();

        if (data.delivery_country === 'Brazil') {
            // remove all characters that are not numbers
            const digitsOnlyCustomReference = data.delivery_customs_reference.replace(/\D/g, '');

            if (!validateCpf(digitsOnlyCustomReference)) {
                setError('delivery_customs_reference', {
                    type: 'custom',
                    message: 'Please enter a valid CPF.',
                });
                return;
            }
            // convert digitsOnly to this format '000.000.000-00'
            const formattedCPF = digitsOnlyCustomReference.replace(
                /(\d{3})(\d{3})(\d{3})(\d{2})/,
                '$1.$2.$3-$4',
            );
            formData.append('delivery_customs_reference', formattedCPF);
        }

        if (data.delivery_country === 'Mexico') {
            const upperCaseCustomReference = data.delivery_customs_reference.toUpperCase();
            if (!validateRFCOrCURP(upperCaseCustomReference)) {
                setError('delivery_customs_reference', {
                    type: 'custom',
                    message: 'Please enter a valid RFC/CURP.',
                });
                return;
            }
            formData.append('delivery_customs_reference', upperCaseCustomReference);
        }

        formData.append('delivery_company', data.delivery_company);
        formData.append('delivery_firstname', data.delivery_firstname);
        formData.append('delivery_lastname', data.delivery_lastname);
        formData.append('delivery_line1', data.delivery_line1);
        formData.append('delivery_line2', data.delivery_line2);
        formData.append('delivery_city', data.delivery_city);
        formData.append('delivery_region', data.delivery_region);
        formData.append('delivery_postcode', data.delivery_postcode);
        formData.append('delivery_country', data.delivery_country);
        formData.append('delivery_phone', data.delivery_phone);
        formData.append('delivery_phone_code', data.delivery_phone_code);

        await fetch(postUrl, {
            method: 'POST',
            body: formData,
        });

        // append ca=1 to url params if it doesnt exist and then reload the page
        if (!window.location.search.includes('ca=1')) {
            window.location.search += '?ca=1';
        } else {
            window.location.reload();
        }
    };

    return (
        <form method='post' name='delivery-confirm-address' onSubmit={handleSubmit(onSubmit)}>
            <div className='modal-header'>
                <h3 className='modal-title'>Please confirm your shipping address, {firstName}</h3>
            </div>
            <div className='modal-body'>
                <p>Once you confirm your address, we will dispatch your Medal.</p>
                <p>
                    Medals are sent via USPS in the US and by FedEx for the rest of the world.
                    Please ensure you are able to accept these types of deliveries.
                </p>
                <p>
                    The address you enter below will be exactly how it appears on the postage label.
                </p>

                <div className='row'>
                    <div className='col-12'>
                        <div className='row mb-3'>
                            <div className='col-9'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={confirmAddress.delivery_company ?? ''}
                                        name='delivery_company'
                                        {...register('delivery_company', {
                                            maxLength: {
                                                value: maxCompanyLength,
                                                message: 'Max length is ' + maxCompanyLength,
                                            },
                                        })}
                                        placeholder='Company Name (if applicable)'
                                        maxLength='30'
                                    />
                                    {errors.delivery_company && (
                                        <p className='text-danger fs-6'>
                                            {errors.delivery_company.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='row mb-3'>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={
                                            confirmAddress.delivery_firstname
                                                ? confirmAddress.delivery_firstname
                                                : firstName ?? ''
                                        }
                                        name='delivery_firstname'
                                        {...register('delivery_firstname', {
                                            required: true,
                                            maxLength: 30,
                                        })}
                                        placeholder='First Name *'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={
                                            confirmAddress.delivery_lastname
                                                ? confirmAddress.delivery_lastname
                                                : lastName ?? ''
                                        }
                                        name='delivery_lastname'
                                        {...register('delivery_lastname', {
                                            required: true,
                                            maxLength: 30,
                                        })}
                                        placeholder='Last Name *'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='row mb-3'>
                            <div className='input-container'>
                                <input
                                    type='text'
                                    className='form-control'
                                    defaultValue={confirmAddress.delivery_line1 ?? ''}
                                    name='delivery_line1'
                                    {...register('delivery_line1', {
                                        required: true,
                                        maxLength: 50,
                                    })}
                                    placeholder='Address Line 1 *'
                                    maxLength='50'
                                />
                            </div>
                        </div>
                        <div className='row mb-3'>
                            <div className='input-container'>
                                <input
                                    type='text'
                                    className='form-control'
                                    defaultValue={confirmAddress.delivery_line2 ?? ''}
                                    name='delivery_line2'
                                    {...register('delivery_line2', { maxLength: 50 })}
                                    placeholder='Address Line 2'
                                    maxLength='50'
                                />
                            </div>
                        </div>

                        <div className='row mb-3'>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={confirmAddress.delivery_city ?? ''}
                                        name='delivery_city'
                                        {...register('delivery_city', {
                                            required: true,
                                            maxLength: 30,
                                        })}
                                        placeholder='City *'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={confirmAddress.delivery_region ?? ''}
                                        name='delivery_region'
                                        {...register('delivery_region', {
                                            required: true,
                                            maxLength: 30,
                                        })}
                                        placeholder='State/County/Region *'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='row mb-3'>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={confirmAddress.delivery_postcode ?? ''}
                                        name='delivery_postcode'
                                        {...register('delivery_postcode', {
                                            required: true,
                                            maxLength: 30,
                                        })}
                                        placeholder='Zip/Postal Code *'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className='input-container'>
                                    <select
                                        name='delivery_country'
                                        {...register('delivery_country', { required: true })}
                                        className='form-select'
                                        defaultValue={confirmAddress.delivery_country ?? ''}>
                                        <option value=''>Country *</option>
                                        {Object.keys(shippingCountries).map((index) => {
                                            const key = shippingCountries[index][0];
                                            const value = shippingCountries[index][1];
                                            return (
                                                <option key={key} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {showCustomsReference && (
                            <div className='row mb-3'>
                                <div className='col-8'>
                                    <div className='input-container'>
                                        <input
                                            type='text'
                                            className='form-control'
                                            defaultValue={
                                                confirmAddress.delivery_customs_reference ?? ''
                                            }
                                            name='delivery_customs_reference'
                                            {...register('delivery_customs_reference', {
                                                required: true,
                                            })}
                                            placeholder={customReferencePlaceholder}
                                            maxLength='20'
                                        />
                                        {errors.delivery_customs_reference?.message && (
                                            <p className='text-danger fs-6'>
                                                {errors.delivery_customs_reference.message}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='row mb-3'>
                            <div className='col-4'>
                                <div className='input-container'>
                                    <select
                                        name='delivery_phone_code'
                                        {...register('delivery_phone_code', { required: true })}
                                        className='form-select'
                                        defaultValue={confirmAddress.delivery_phone_code ?? ''}>
                                        <option value=''>Phone code *</option>
                                        {shippingDialCodes.map((dialCode) => {
                                            return (
                                                <option key={dialCode} value={dialCode}>
                                                    {dialCode}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                            <div className='col-8'>
                                <div className='control-group'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        defaultValue={confirmAddress.delivery_phone ?? ''}
                                        name='delivery_phone'
                                        {...register('delivery_phone', { required: true })}
                                        placeholder='Phone Number'
                                        maxLength='30'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='modal-footer'>
                <button type='submit' className='bg-img-btn blue-img'>
                    CONFIRM ADDRESS
                </button>
            </div>
        </form>
    );
};

const rctShippingAddressForm = document.getElementById('rct-shipping-address-form');

if (rctShippingAddressForm !== null) {
    ReactDOMClient.createRoot(rctShippingAddressForm).render(<ShippingAddressForm />);
}
